"use client";

import { useEffect, useState } from "react";

import { cn } from "@/lib/utils/cn";

import {
  FORMULA_OPERATOR_OPTIONS,
  formatFormulaExpression,
  type ChannelFormula,
  type ChannelFormulaOperator,
  type ChannelRow,
} from "../schema/datalogger.schema";

type ChannelFormulaModalProps = {
  open: boolean;
  onClose: () => void;
  currentChannelId: string;
  channels: ChannelRow[];
  initial: ChannelFormula | null;
  onSave: (f: ChannelFormula) => void;
};

export function ChannelFormulaModal({
  open,
  onClose,
  currentChannelId,
  channels,
  initial,
  onSave,
}: ChannelFormulaModalProps) {
  const sources = channels.filter((c) => c.id !== currentChannelId);
  const [sourceKey, setSourceKey] = useState("");
  const [operator, setOperator] = useState<ChannelFormulaOperator>("add");
  const [operand, setOperand] = useState("0");

  useEffect(() => {
    if (!open) {
      return;
    }
    const srcList = channels.filter((c) => c.id !== currentChannelId);
    const first = srcList[0]?.channelKey ?? "";
    setSourceKey(initial?.sourceChannelKey || first);
    setOperator(initial?.operator ?? "add");
    setOperand(String(initial?.operand ?? 0));
  }, [open, initial, currentChannelId, channels]);

  if (!open) {
    return null;
  }

  const previewFormula: ChannelFormula = {
    sourceChannelKey: sourceKey,
    operator,
    operand: Number(operand),
  };

  const previewOk =
    sourceKey &&
    sources.some((c) => c.channelKey === sourceKey) &&
    Number.isFinite(previewFormula.operand) &&
    !(operator === "divide" && previewFormula.operand === 0);

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-control border border-card-border bg-surface-primary shadow-card"
        role="dialog"
        aria-modal
        aria-labelledby="formula-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-card-border bg-muted-bg/30 px-ds-6 py-ds-5">
          <h2
            id="formula-modal-title"
            className="font-sans text-lg font-semibold text-neutral-950 dark:text-foreground"
          >
            Configure formula
          </h2>
          <p className="mt-ds-2 text-caption text-neutral-700 dark:text-muted">
            Derive this channel from another channel with a simple arithmetic step such as scaling or offset.
          </p>
        </div>

        <div className="px-ds-6 py-ds-5">
          {sources.length === 0 ? (
            <p className="rounded-control border border-warning/35 bg-warning/10 px-ds-3 py-ds-3 text-caption text-warning">
              Add another channel first. Formulas need a source channel to reference.
            </p>
          ) : (
            <div className="flex flex-col gap-ds-4">
              <div className="rounded-[20px] border border-card-border bg-muted-bg/30 p-ds-4">
                <div className="flex flex-col gap-ds-2">
                  <label className="form-label">Source channel</label>
                  <select
                    className="ui-control w-full rounded-control bg-surface-primary text-foreground"
                    value={sourceKey}
                    onChange={(e) => setSourceKey(e.target.value)}
                  >
                    {sources.map((c) => (
                      <option key={c.id} value={c.channelKey}>
                        {c.name.trim() || c.channelKey} ({c.channelKey})
                      </option>
                    ))}
                  </select>
                  <span className="form-helper">Choose the existing channel this derived value should read from.</span>
                </div>
              </div>

              <div className="grid gap-ds-4 rounded-[20px] border border-card-border bg-muted-bg/30 p-ds-4">
                <div className="flex flex-col gap-ds-2">
                  <label className="form-label">Operation</label>
                  <select
                    className="ui-control w-full rounded-control bg-surface-primary text-foreground"
                    value={operator}
                    onChange={(e) => setOperator(e.target.value as ChannelFormulaOperator)}
                  >
                    {FORMULA_OPERATOR_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <span className="form-helper">Pick how the source value should be adjusted before it is stored or displayed.</span>
                </div>

                <div className="flex flex-col gap-ds-2">
                  <label className="form-label">Operand (number)</label>
                  <input
                    className="ui-control w-full rounded-control tabular-nums text-foreground"
                    value={operand}
                    onChange={(e) => setOperand(e.target.value)}
                    inputMode="decimal"
                  />
                  <span className="form-helper">Use positive or negative values depending on the operation you selected.</span>
                </div>
              </div>

              <div className="rounded-[20px] border border-card-border bg-muted-bg/30 px-ds-4 py-ds-4 dark:border-border dark:bg-muted-bg/40">
                <p className="form-label">Preview</p>
                <p className="mt-ds-1 text-sm text-neutral-950 dark:text-foreground">
                  {previewOk ? formatFormulaExpression(previewFormula) : "-"}
                </p>
                <p className="mt-ds-2 text-[11px] leading-5 text-muted">
                  This preview helps confirm the expression before you save it to the channel.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-wrap justify-end gap-ds-2 border-t border-card-border bg-muted-bg/30 px-ds-6 py-ds-4">
          <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={cn("btn btn-primary btn-sm !no-underline", (!previewOk || sources.length === 0) && "pointer-events-none opacity-50")}
            disabled={!previewOk || sources.length === 0}
            onClick={() => {
              onSave(previewFormula);
              onClose();
            }}
          >
            Save formula
          </button>
        </div>
      </div>
    </div>
  );
}
